import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Plus, Pencil, Trash2, Building2, ChevronRight } from "lucide-react";
import { toast } from "sonner";

/**
 * Phase 48 — Departments admin.
 * Tree view (parent → children). Admin can add, rename, reparent, deactivate.
 */
export default function DepartmentsPage() {
  const navigate = useNavigate();
  const { hasPerm } = useAuth();
  const canManage = hasPerm("departments.manage");

  const [items, setItems] = useState([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(null); // dept obj
  const [form, setForm] = useState({ name: "", description: "", parent_id: "", color: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/departments?include_inactive=${includeInactive}`);
      setItems(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load departments");
    } finally { setLoading(false); }
  }, [includeInactive]);
  useEffect(() => { load(); }, [load]);

  // Build tree from flat list
  const buildTree = (list) => {
    const byId = Object.fromEntries(list.map((d) => [d.id, { ...d, children: [] }]));
    const roots = [];
    for (const d of list) {
      const node = byId[d.id];
      if (d.parent_id && byId[d.parent_id]) byId[d.parent_id].children.push(node);
      else roots.push(node);
    }
    // Sort each level alphabetically
    const sortRec = (nodes) => {
      nodes.sort((a, b) => a.name.localeCompare(b.name));
      nodes.forEach((n) => sortRec(n.children));
    };
    sortRec(roots);
    return roots;
  };
  const tree = buildTree(items);

  const openCreate = (parent_id = "") => {
    setForm({ name: "", description: "", parent_id, color: "" });
    setEditOpen(null);
    setCreateOpen(true);
  };
  const openEdit = (d) => {
    setForm({
      name: d.name,
      description: d.description || "",
      parent_id: d.parent_id || "",
      color: d.color || "",
    });
    setEditOpen(d);
    setCreateOpen(false);
  };

  const submitCreate = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        parent_id: form.parent_id || null,
        color: form.color || null,
      };
      await api.post("/departments", payload);
      toast.success("Department added");
      setCreateOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally { setSubmitting(false); }
  };

  const submitEdit = async () => {
    if (!editOpen) return;
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setSubmitting(true);
    try {
      await api.put(`/departments/${editOpen.id}`, {
        name: form.name.trim(),
        description: form.description.trim(),
        parent_id: form.parent_id || null,
        color: form.color || null,
      });
      toast.success("Department updated");
      setEditOpen(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setSubmitting(false); }
  };

  const deactivate = async (d) => {
    if (!window.confirm(`Deactivate department "${d.name}"?\n\nHistorical requests using this department will keep displaying it. You can reactivate later by editing.`)) return;
    try {
      await api.delete(`/departments/${d.id}`);
      toast.success("Department deactivated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const reactivate = async (d) => {
    try {
      await api.put(`/departments/${d.id}`, { is_active: true });
      toast.success(`"${d.name}" reactivated`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  // Flatten tree into rows for rendering: depth-first, preserving order
  const flattenTree = (nodes, depth = 0, acc = []) => {
    for (const n of nodes) {
      acc.push({ ...n, depth });
      if (n.children && n.children.length > 0) flattenTree(n.children, depth + 1, acc);
    }
    return acc;
  };
  const rows = flattenTree(tree);

  return (
    <div className="space-y-6 fade-in" data-testid="departments-page">
      <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2 -ml-2 text-[#525C58]" data-testid="depts-back-btn">
        <ArrowLeft className="h-4 w-4" /> Back
      </Button>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>Departments</h1>
          <p className="text-[#525C58] mt-1">Organise approvers by department. Sub-departments inherit their parent's head — e.g. a Pediatrics request can be approved by the Medicine head if Pediatrics is nested under Medicine.</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-[#525C58] cursor-pointer">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              data-testid="depts-show-inactive"
            />
            Show inactive
          </label>
          {canManage && (
            <Button onClick={() => openCreate("")} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="add-dept-btn">
              <Plus className="h-4 w-4 mr-2" /> Add department
            </Button>
          )}
        </div>
      </div>

      <Card className="border-[#E8E8E6]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Building2 className="h-5 w-5 text-[#40916C]" /> Department tree ({items.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? <p className="py-10 text-center text-[#7A8A84]">Loading…</p> :
           rows.length === 0 ? <p className="py-10 text-center text-[#7A8A84]">No departments. Add one to get started.</p> : (
            <ul className="space-y-1" data-testid="depts-tree">
              {rows.map((node) => (
                <li
                  key={node.id}
                  className={`flex items-center gap-2 py-2 px-2 rounded hover:bg-[#FAFAF8] ${node.is_active ? "" : "opacity-60"}`}
                  style={{ paddingLeft: `${node.depth * 24 + 8}px` }}
                  data-testid={`dept-node-${node.id}`}
                >
                  {node.depth > 0 && <ChevronRight className="h-3.5 w-3.5 text-[#A3ACA8] shrink-0" />}
                  <Building2 className="h-4 w-4 text-[#40916C] shrink-0" />
                  <span className="text-sm font-medium text-[#1B4332]">{node.name}</span>
                  {!node.is_active && <Badge className="bg-gray-100 text-gray-600 border border-gray-200 text-[10px]">inactive</Badge>}
                  {node.description && <span className="text-xs text-[#7A8A84] hidden md:inline">— {node.description}</span>}
                  <div className="ml-auto flex items-center gap-1">
                    {canManage && node.is_active && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => openCreate(node.id)} className="h-7 px-2 text-xs text-[#525C58]" title="Add sub-department" data-testid={`dept-add-sub-${node.id}`}>
                          <Plus className="h-3 w-3 mr-1" /> sub
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(node)} className="h-7 w-7 p-0" data-testid={`dept-edit-${node.id}`}>
                          <Pencil className="h-3 w-3 text-[#525C58]" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deactivate(node)} className="h-7 w-7 p-0 hover:bg-red-50" data-testid={`dept-delete-${node.id}`}>
                          <Trash2 className="h-3 w-3 text-red-500" />
                        </Button>
                      </>
                    )}
                    {canManage && !node.is_active && (
                      <Button size="sm" variant="ghost" onClick={() => reactivate(node)} className="h-7 px-2 text-xs text-emerald-700 hover:bg-emerald-50" data-testid={`dept-reactivate-${node.id}`}>
                        Reactivate
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={createOpen || !!editOpen} onOpenChange={(o) => { if (!o) { setCreateOpen(false); setEditOpen(null); }}}>
        <DialogContent className="max-w-md" data-testid="dept-form-dialog">
          <DialogHeader>
            <DialogTitle>{editOpen ? "Edit department" : "Add department"}</DialogTitle>
            <DialogDescription>
              {editOpen ? "Rename, reparent, or update description." : "Add a new department or sub-department to your org."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Cardiology" data-testid="dept-name-input" />
            </div>
            <div className="space-y-1">
              <Label>Description (optional)</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Short description shown to approvers" data-testid="dept-desc-input" />
            </div>
            <div className="space-y-1">
              <Label>Parent department (optional)</Label>
              <Select value={form.parent_id || "none"} onValueChange={(v) => setForm({ ...form, parent_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="dept-parent-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Top-level (no parent)</SelectItem>
                  {items.filter((d) => !editOpen || d.id !== editOpen.id).map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-[#7A8A84]">Sub-departments inherit their parent's approver. Leave empty for a top-level department.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateOpen(false); setEditOpen(null); }}>Cancel</Button>
            <Button
              onClick={editOpen ? submitEdit : submitCreate}
              disabled={submitting}
              className="bg-[#1B4332] hover:bg-[#2D6A4F]"
              data-testid="dept-save-btn"
            >
              {submitting ? "Saving…" : (editOpen ? "Save changes" : "Add department")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DeptNodeUnused() { return null; }
// (recursive component replaced with flat-iterative rendering above to avoid Babel
// AST traversal stack overflow that recursive JSX can trigger.)
